import dagre from 'dagre'
import type { Edge, Node } from '@xyflow/react'
import type { GraphNodeData, InlineField, PointerField } from '../types'

const NODE_WIDTH = 440
const MIN_NODE_HEIGHT = 60
const HEADER_HEIGHT = 44
const PADDING = 14

// Roughly how many characters fit on one line inside a value cell
const CHARS_PER_LINE = 52
const VALUE_LINE_HEIGHT = 22
const POINTER_ROW_HEIGHT = 30

function estimateRowHeight(field: InlineField): number {
  const valStr =
    field.value === null || field.value === undefined ? '' : String(field.value)
  // Use the LONGER of key and value as the dominant wrap driver
  const chars = Math.max(field.key.length + 2, valStr.length)
  const lines = Math.max(1, Math.ceil(chars / CHARS_PER_LINE))
  return 14 + lines * VALUE_LINE_HEIGHT
}

function estimatePointerRowHeight(_field: PointerField): number {
  return POINTER_ROW_HEIGHT
}

function estimateNodeHeight(data: GraphNodeData): number {
  if (data.collapsed) return HEADER_HEIGHT + 4
  if (data.kind === 'value') {
    const s = data.scalarValue
    const str = s === null || s === undefined ? '' : String(s)
    const lines = Math.max(1, Math.ceil(str.length / CHARS_PER_LINE))
    return HEADER_HEIGHT + lines * VALUE_LINE_HEIGHT + PADDING * 2
  }
  let content = 0
  for (const f of data.inlineFields) content += estimateRowHeight(f)
  for (const p of data.pointerFields) content += estimatePointerRowHeight(p)
  // Add-key row / bottom padding
  if (data.kind === 'object') content += 28
  return HEADER_HEIGHT + Math.max(MIN_NODE_HEIGHT, content) + PADDING
}

export function autoLayout(
  nodes: Node<GraphNodeData>[],
  edges: Edge[],
): Node<GraphNodeData>[] {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 44, ranksep: 140, marginx: 40, marginy: 40 })

  for (const n of nodes) {
    const measured = (n as { measured?: { width?: number; height?: number } }).measured
    const width = measured?.width ?? NODE_WIDTH
    const height = measured?.height ?? estimateNodeHeight(n.data)
    g.setNode(n.id, { width, height })
  }
  for (const e of edges) g.setEdge(e.source, e.target)
  dagre.layout(g)

  return nodes.map((n) => {
    const pos = g.node(n.id)
    const measured = (n as { measured?: { width?: number; height?: number } }).measured
    const w = measured?.width ?? NODE_WIDTH
    const h = measured?.height ?? estimateNodeHeight(n.data)
    return { ...n, position: { x: pos.x - w / 2, y: pos.y - h / 2 } }
  })
}

export const NODE_LAYOUT_WIDTH = NODE_WIDTH
